import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { LIVE_REFETCH_MS, queryKeys } from "@/api/queryClient";
import type { Cell, CustomerSpec, ProvisionAuditEvent, Release } from "@/types";

// #343 — Leser der Provisionierung: Zellen, Spezifikationen (Live-Sicht, der Status kommt vom Zellen-Agenten),
// Releases, Audit.
function useClient() {
  const { session, logout } = useAuth();
  return { session, client: () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout }) };
}

export function useCells(): UseQueryResult<Cell[]> {
  const { session, client } = useClient();
  return useQuery({ queryKey: queryKeys.list("cells"), queryFn: () => client().get<Cell[]>("/cells"), enabled: !!session, refetchInterval: LIVE_REFETCH_MS });
}

export function useCell(id: string | undefined): UseQueryResult<Cell> {
  const { session, client } = useClient();
  return useQuery({
    queryKey: id ? queryKeys.detail("cells", id) : ["resources", "cells", "_invalid"],
    queryFn: () => client().get<Cell>(`/cells/${encodeURIComponent(id ?? "")}`),
    enabled: !!id && !!session,
    refetchInterval: LIVE_REFETCH_MS,
  });
}

export function useSpecs(filter: { cellId?: string; customerId?: string } = {}): UseQueryResult<CustomerSpec[]> {
  const { session, client } = useClient();
  const q = filter.cellId ? `?cellId=${encodeURIComponent(filter.cellId)}` : filter.customerId ? `?customerId=${encodeURIComponent(filter.customerId)}` : "";
  return useQuery({
    queryKey: q ? [...queryKeys.list("customer-specs"), q] : queryKeys.list("customer-specs"),
    queryFn: () => client().get<CustomerSpec[]>(`/customer-specs${q}`),
    enabled: !!session,
    refetchInterval: LIVE_REFETCH_MS,
  });
}

export function useSpec(id: string | undefined): UseQueryResult<CustomerSpec> {
  const { session, client } = useClient();
  return useQuery({
    queryKey: id ? queryKeys.detail("customer-specs", id) : ["resources", "customer-specs", "_invalid"],
    queryFn: () => client().get<CustomerSpec>(`/customer-specs/${encodeURIComponent(id ?? "")}`),
    enabled: !!id && !!session,
    refetchInterval: LIVE_REFETCH_MS,
  });
}

export function useReleases(): UseQueryResult<Release[]> {
  const { session, client } = useClient();
  return useQuery({ queryKey: queryKeys.list("releases"), queryFn: () => client().get<Release[]>("/releases"), enabled: !!session });
}

export function useProvisionAudit(filter: { cellId?: string; specId?: string; limit?: number }): UseQueryResult<ProvisionAuditEvent[]> {
  const { session, client } = useClient();
  const params = new URLSearchParams();
  if (filter.cellId) params.set("cellId", filter.cellId);
  if (filter.specId) params.set("specId", filter.specId);
  params.set("limit", String(filter.limit ?? 50));
  const q = `?${params.toString()}`;
  return useQuery({
    queryKey: [...queryKeys.list("provision-audit"), q],
    queryFn: () => client().get<ProvisionAuditEvent[]>(`/provision-audit${q}`),
    enabled: !!session && Boolean(filter.cellId || filter.specId),
    refetchInterval: LIVE_REFETCH_MS,
  });
}

// Vorbelegungen fuer das Anlege-Formular: die Kunden-Domain (`<kennung>.<domain>`). Optional am Wire (aelteres Portal).
export function useSpecDefaults(): UseQueryResult<{ customerDomain: string | null }> {
  const { session, client } = useClient();
  return useQuery({
    queryKey: ["resources", "customer-specs", "defaults"],
    queryFn: () => client().get<{ customerDomain: string | null }>("/customer-specs/defaults"),
    enabled: !!session,
    staleTime: 5 * 60_000,
  });
}
